import React, { createContext, useContext, ReactNode } from 'react';
import { useTheme as useThemeHook } from '@ai-tutor/ui';

interface ThemeConfig {
  mode: 'light' | 'dark' | 'system';
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  borderRadius: number;
  customVariables?: Record<string, string>;
}

interface ThemeContextValue {
  theme: ThemeConfig;
  setTheme: (theme: Partial<ThemeConfig>) => void;
  toggleMode: () => void;
  resetTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Partial<ThemeConfig>;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme,
}) => {
  const themeHook = useThemeHook();

  // Apply default theme if provided.
  //
  // IMPORTANT: never force `mode` from the default here. The useTheme hook
  // has already restored the persisted mode from localStorage on mount, and
  // because child effects run before parent effects, applying a default that
  // carried `mode: 'light'` would clobber a saved dark preference on every
  // reload. So we seed only the non-mode defaults (primaryColor, fontFamily,
  // borderRadius, customVariables) and leave the user's mode untouched.
  React.useEffect(() => {
    if (defaultTheme) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { mode: _ignoredMode, ...defaultsWithoutMode } = defaultTheme;
      themeHook.setTheme(defaultsWithoutMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTheme]); // themeHook.setTheme is stable from useTheme hook

  const value: ThemeContextValue = {
    theme: themeHook.theme,
    setTheme: themeHook.setTheme,
    toggleMode: themeHook.toggleMode,
    resetTheme: themeHook.resetTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * The theme when a `ThemeProvider` is mounted, else `undefined` - for UI that
 * only DISPLAYS the mode (the phone Settings list's Appearance hint) and so
 * should not throw where no provider is (bare stories, tests).
 */
export const useOptionalTheme = (): ThemeContextValue | undefined =>
  useContext(ThemeContext);
